import { ActivatedRoute, Router } from '@angular/router';
import { ApiServiceService } from '../../../api-service.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Keys, StorageService } from '../../../service/storage.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MathUtil } from '../../../utils/math.util';
import { Emi, EmiSchedule } from '../../../model/emi-schedule.model';
import { EmiUtil } from '../../../utils/emi.util';
import { BreadcrumbLabel, HasBreadcrumb } from '../breadcrumbs/breadcrumb';
import { LoanDetailsService } from '../../../service/loan-details.service';
import { HttpClient } from '@angular/common/http';
import * as moment from 'moment';
import { LoanRestructure } from '../../../model/loan-restructure';
import { LoanRestructureService } from '../../../service/loan-restructure.service';
import { LoanUtil } from '../../../utils/loan.util';
import { ZipLoanStatus } from '../../../model/ZipLoanStatus.model';
import { MatDialog, MatDialogConfig } from '@angular/material';
import { AddFundPopupV2Component } from '../../../shared/add-fund-popup-v2/add-fund-popup-v2.component';
import { PaymentProduct } from '../../../model/payment/PaymentCollectionRequest';

@Component({
    selector: 'app-disbursed-loan-info',
    templateUrl: './disbursed-loan-info.component.html',
    styleUrls: ['./disbursed-loan-info.component.scss'],
    providers: [ApiServiceService, EmiUtil]
})
export class DisbursedLoanInfoComponent implements OnInit, HasBreadcrumb {
    result2: any;
    result: any;
    name: string;
    photo: string;
    loanAmt: number;
    emi_amount: number = 0;
    roi: number;
    loan_tenure: number;
    loan_fulfilled_amount: any;
    data: any = [];
    fractionSize: any;
    repaidAmt: number;
    amtDue: number;
    finzyDomain = true;
    otherDomain = false;
    ifPrePayment = false;
    isZipDisbursedLoan = false;
    loanId: string;
    latestLoanRestructuring: LoanRestructure;
    displayableLoanSanctionStatus: string;
    displayedColumns: any = ['due_on', 'emi_amount', 'balance_amount', 'remaining_tenure', 'emi_status'];
    loanAmount: number;

    private readonly matDialog: MatDialog;

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    dataSource: any;

    constructor(private http: HttpClient, private router: Router, private ref: ApiServiceService,
        private storageService: StorageService, private emiUtil: EmiUtil, private route: ActivatedRoute,
        private loanService: LoanDetailsService, private loanRestructureService: LoanRestructureService,
        matDialog: MatDialog) {
        this.matDialog = matDialog;
    }

    async ngOnInit() {
        this.loanId = this.route.snapshot.params.loanId;

        this.http.post<any>(this.ref.baseURL() + 'getEmiDetails',
            {
                loan_id: this.loanId,
                token: this.storageService.getItem(Keys.TOKEN),
                profile_id: this.storageService.getItem(Keys.PROFILE_ID)
            }).subscribe(async (result2) => {
                this.result2 = result2;
                if (this.result2.status_code === 200 && this.result2.status === 'success') {
                    this.result2.response[0].emi_schedule.forEach(emi => {
                        emi['emi_displayable_status'] = this.getPaymentStatus(emi);
                        this.data.push(emi);
                    });
                    this.dataSource = new MatTableDataSource(this.data);
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;

                    this.displayableLoanSanctionStatus = LoanUtil.getDisplayableLoanStatus(this.result2.response[0].loan_sanction_status, !!this.result2.response[0].loanRestructuredOn, 100);

                    this.roi =
                        MathUtil.roundOff((this.result2.response[0].effective_roi === undefined || this.result2.response[0].effective_roi === null) ?
                            this.result2.response[0].roi : this.result2.response[0].effective_roi);
                    this.loan_tenure = this.result2.response[0].loan_tenure;

                    this.isZipDisbursedLoan = ZipLoanStatus.isZipDisbursedLoan(this.result2.response[0].loan_sanction_status, this.result2.response[0].loan_disbursal_status);
                    if (!this.isZipDisbursedLoan) {
                        this.emi_amount = this.getLatestEmi(this.result2.response[0].emi_schedule);
                    }
                    if (this.result2.response[0].loanRestructuredOn) {
                        this.latestLoanRestructuring = await this.loanRestructureService.getLatestRestructuringByLoanId(this.loanId);
                    }

                    this.amtDue = this.result2.amtDue;
                    this.repaidAmt = this.result2.repaidAmt;

                    if (this.result2.response[0].loan_disbursed_amount === 0) {
                        this.loan_fulfilled_amount = '--';
                    } else if (this.latestLoanRestructuring) {
                        this.loan_fulfilled_amount = this.latestLoanRestructuring.principal + this.latestLoanRestructuring.capitalizedInterestRestructure;
                    } else {
                        this.loan_fulfilled_amount = this.result2.response[0].loan_fulfilled_amount;
                    }
                    this.loanAmt = this.result2.response[0].loan_fulfilled_amount;
                    if (this.result2.response[0].loan_sanction_status === 'closed_prepayment') {
                        this.ifPrePayment = true;
                    }
                    this.loanAmount = this.result2.response[0].loan_amount;
                }
            });
    }

    public getLatestEmi(emiSchedule: EmiSchedule): number {
        const referenceDate = moment().toDate();
        const upcomingEmi = this.emiUtil.findNextEmi(emiSchedule, referenceDate);
        return upcomingEmi ? this.getApplicableEffectiveEmiAmount(upcomingEmi) : null;
    }

    public getApplicablePrincipalOutstanding(emi: any): number {
        if (emi.subvention !== undefined && emi.subvention != null) {
            return emi.subvention.balance_amount;
        }
        return emi.balance_amount;
    }

    private getApplicableEffectiveEmiAmount(upcomingEmi) {
        if (upcomingEmi.subvention !== undefined && upcomingEmi.subvention != null) {
            return upcomingEmi.subvention.emi_amount;
        }
        return upcomingEmi.emi_amount;
    }

    async breadcrumb(): Promise<BreadcrumbLabel | string> {
        const loanId = this.route.snapshot.params.loanId;
        return (await this.loanService.getLoanDetails(loanId)).loan_info.generated_loan_id;
    }

    getEmiScheduleLabel(): string {
        return this.latestLoanRestructuring ? 'Restructured EMI Schedule' : 'EMI Schedule';
    }

    getPaymentStatus(emi: Emi): string {
        if (this.emiUtil.isEmiPaid(emi)) {
            return 'Paid';
        } else if (moment(emi.due_on).isBefore(moment())) {
            return 'Unpaid';
        } else {
            return 'Upcoming';
        }
    }

    payEmiHandler() {
        console.log("payYourEmiHandler")
        const matDialogConfig: MatDialogConfig = {
            maxHeight: '90vh',
            width: '80%',
            panelClass: "dialog-responsive",
            data: {
                dialogHeading: `Make Your Payments`,
                dialogSubHeading: `You can pay EMIs or Part/Full Prepay your loan <br> Part prepayments should be in multiples of <strong style="color: #000;">₹1000 </strong>`,
                bodyNotifySupportEmail: `Please notify us at <a href="mailto:support@finzy.com" target="_blank">support@finzy.com</a>`,
                bodyNormalContent: `If you have multiple loans and you are transferring EMIs or making prepayment in one transfer and 
                                    if you want to apply the funds transferred to certain specific loan(s) as soon as you complete the transfer`,
                bodyTitle: `You can pay your EMI by choosing an online payment method`,
                bodyEnterAmount: `Enter Amount:`,
                payButtonText: `PAY NOW`,
                amountWarningText: `Please enter the valid amount`,
                tableHeading: `You can pay your EMI by <span style="font-weight: bolder;">transferring funds</span>to this bank`,
                paymentProduct: PaymentProduct.LOAN_EMI_PAYMENT,
                bankDetails: {
                    'Account Name': 'Bridge fintech solutions Pvt Ltd',
                    'Bank': 'ICICI Bank',
                    'Virtual Account Number':'2FINZYRAKS021818',
                    'Account type':'Current account',
                    'IFSC code':'ICIC0000104'
                },
                hideBankDetails: true
            }
        };
        this.matDialog.open(AddFundPopupV2Component, matDialogConfig).afterClosed().subscribe(response => {
            console.log(response)
        })
    }

    prePayHandler() {
        console.log("prePartyPayHandler")
        const matDialogConfig: MatDialogConfig = {
            maxHeight: '90vh',
            width: '80%',
            panelClass: "dialog-responsive",
            data: {
                dialogHeading: `Make Your Payments`,
                dialogSubHeading: `You can pay EMIs or Part/Full Prepay your loan <br> Part prepayments should be in multiples of <strong style="color: #000;">₹1000 </strong>`,
                bodyNotifySupportEmail: `Please notify us at <a href="mailto:support@finzy.com" target="_blank">support@finzy.com</a>`,
                bodyNormalContent: `If you have multiple loans and you are transferring EMIs or making prepayment in one transfer and 
                                    if you want to apply the funds transferred to certain specific loan(s) as soon as you complete the transfer`,
                bodyTitle: `You can pay your EMI by choosing an online payment method`,
                bodyEnterAmount: `Enter Amount:`,
                payButtonText: `PAY NOW`,
                amountWarningText: `Please enter the valid amount`,
                tableHeading: `You can pay your EMI by <span style="font-weight: bolder;">transferring funds</span>to this bank`,
                paymentProduct: PaymentProduct.LOAN_EMI_PAYMENT,
                bankDetails: {
                    'Account Name': 'Bridge fintech solutions Pvt Ltd',
                    'Bank': 'ICICI Bank',
                    'Virtual Account Number':'2FINZYRAKS021818',
                    'Account type':'Current account',
                    'IFSC code':'ICIC0000104'
                },
                hideBankDetails: true
            }
        };
        this.matDialog.open(AddFundPopupV2Component, matDialogConfig).afterClosed().subscribe(response => {
            console.log(response)
        })
    }
}
